import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { useGenerateCardPool } from '../../hooks';
import { formatPoints } from '../../lib/format';
import { GENERATE_MODE_LABELS, GENERATE_MODE_OPTIONS } from '../../model/labels';
import type { GenerateCardPoolRequest, GenerateMode } from '../../model/types';

interface GeneratePoolDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
}

const toNum = (value: string): number | undefined => (value.trim() ? Number(value) : undefined);

/** Generate a free-card pool by range or mask (gated by `loyalty.manage`). */
export function GeneratePoolDialog({ open, onClose, onDone }: GeneratePoolDialogProps) {
  const [mode, setMode] = useState<GenerateMode>('RANGE');
  const [prefix, setPrefix] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [padLength, setPadLength] = useState('');
  const [mask, setMask] = useState('');
  const [count, setCount] = useState('');
  const [startAt, setStartAt] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const generate = useGenerateCardPool();

  useEffect(() => {
    if (open) {
      setMode('RANGE');
      setPrefix('');
      setStart('');
      setEnd('');
      setPadLength('');
      setMask('');
      setCount('');
      setStartAt('0');
      setError(null);
    }
  }, [open]);

  const startNum = toNum(start);
  const endNum = toNum(end);
  const countNum = toNum(count);
  const rangeValid =
    Number.isInteger(startNum) && Number.isInteger(endNum) && (startNum as number) <= (endNum as number);
  const maskValid = mask.includes('#') && Number.isInteger(countNum) && (countNum as number) >= 1;
  const valid = mode === 'RANGE' ? rangeValid : maskValid;

  const submit = () => {
    setError(null);
    const body: GenerateCardPoolRequest =
      mode === 'RANGE'
        ? {
            mode,
            prefix: prefix.trim() || undefined,
            start: startNum,
            end: endNum,
            padLength: toNum(padLength),
          }
        : { mode, mask: mask.trim(), count: countNum, startAt: toNum(startAt) ?? 0 };
    generate.mutate(body, {
      onSuccess: (res) => {
        onDone(`Генерация пула: добавлено ${formatPoints(res.added)} из ${formatPoints(res.received)}.`);
        onClose();
      },
      onError: (err) => setError(err.message),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Генерация пула карт</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            select
            label="Режим"
            value={mode}
            onChange={(e) => setMode(e.target.value as GenerateMode)}
            fullWidth
          >
            {GENERATE_MODE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {GENERATE_MODE_LABELS[option]}
              </MenuItem>
            ))}
          </TextField>
          {mode === 'RANGE' ? (
            <>
              <TextField
                label="Префикс"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                fullWidth
                helperText="Необязательно — добавляется к каждому номеру"
              />
              <TextField
                label="Начало"
                type="number"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                fullWidth
              />
              <TextField
                label="Конец"
                type="number"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                fullWidth
              />
              <TextField
                label="Дополнить нулями до"
                type="number"
                value={padLength}
                onChange={(e) => setPadLength(e.target.value)}
                fullWidth
                helperText="Ширина числовой части"
              />
            </>
          ) : (
            <>
              <TextField
                label="Маска"
                value={mask}
                onChange={(e) => setMask(e.target.value)}
                error={mask.trim().length > 0 && !mask.includes('#')}
                fullWidth
                helperText="Каждый # — цифра счётчика, напр. 2000####"
                slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
              />
              <TextField
                label="Количество"
                type="number"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                fullWidth
              />
              <TextField
                label="Счётчик с"
                type="number"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                fullWidth
              />
            </>
          )}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={generate.isPending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || generate.isPending}
          startIcon={generate.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Сгенерировать
        </Button>
      </DialogActions>
    </Dialog>
  );
}
